import {
  formatLockBoundary,
  getFirstAllowableCapitalizationStartMonthYear,
  isCapitalizationEndDateLocked,
  isCapitalizationStartDateLocked,
  isCapitalizationStatusLocked,
  isMonthYearBefore,
  isMonthYearBeforeOrEqual,
  maxMonthYear,
  MonthYear,
  stringToMonthYear,
} from '@common/neo.tax';

import { type AscProject } from '@neo.tax/asc/hooks';

import {
  validateCapitalizationEndMonthYear,
  validateCapitalizationStartMonthYear,
} from '../asc-project-detail-form/asc-project-detail-form.utils';

export type CapitalizationFieldConfig = {
  isLocked: boolean;
  disabled: boolean;
  clearable: boolean;
  min: MonthYear | undefined;
  max: MonthYear | undefined;
  helpText: string;
  validate: (value: MonthYear | null) => string | undefined;
};

export type StatusFieldConfig = {
  isLocked: boolean;
  disabled: boolean;
  helpText: string | null;
};

export type UseCapitalizationFieldConfigResult = {
  status: StatusFieldConfig;
  startDate: CapitalizationFieldConfig;
  endDate: CapitalizationFieldConfig;
};

type UseCapitalizationFieldConfigParams = {
  ascProject: AscProject;
  lockBoundary: string | null;
  capitalizationStartMonthYear: MonthYear | null;
  capitalizationEndMonthYear: MonthYear | null;
};

/**
 * Hook that encapsulates all capitalization field configuration logic related to lock boundaries.
 * Returns configuration objects for each field (status, startDate, endDate) with computed
 * properties for disabled, clearable, min/max constraints, helpText, and validation.
 */
export const useCapitalizationFieldConfig = ({
  ascProject,
  capitalizationEndMonthYear,
  capitalizationStartMonthYear,
  lockBoundary,
}: UseCapitalizationFieldConfigParams): UseCapitalizationFieldConfigResult => {
  // Calculate lock states
  const isStatusLocked = isCapitalizationStatusLocked(ascProject.capitalizationStartDate ?? null, lockBoundary);
  const isStartDateLocked = isCapitalizationStartDateLocked(ascProject.capitalizationStartDate ?? null, lockBoundary);
  const isEndDateLocked = isCapitalizationEndDateLocked(ascProject.capitalizationEndDate ?? null, lockBoundary);

  // Convert lock boundary to MonthYear for comparisons
  const lockBoundaryMonthYear = lockBoundary ? stringToMonthYear(lockBoundary) : null;

  // Format lock boundary for display in help text
  const formattedLockBoundary = lockBoundary ? formatLockBoundary(lockBoundary) : null;

  // Status field configuration
  const status: StatusFieldConfig = {
    isLocked: isStatusLocked,
    disabled: isStatusLocked,
    helpText:
      isStatusLocked && formattedLockBoundary
        ? `This field cannot be changed because periods through ${formattedLockBoundary} are closed.`
        : null,
  };

  // Start date field configuration
  const startDate: CapitalizationFieldConfig = {
    isLocked: isStartDateLocked,
    disabled: !ascProject.startDate || isStartDateLocked,
    clearable: !isStartDateLocked,
    // Only apply lock boundary min when field is editable
    min: isStartDateLocked ? undefined : (getFirstAllowableCapitalizationStartMonthYear(lockBoundary) ?? undefined),
    max: capitalizationEndMonthYear ?? undefined,
    helpText:
      isStartDateLocked && formattedLockBoundary
        ? `This field cannot be changed because it is in a closed period (before ${formattedLockBoundary}).`
        : 'Transition from Discovery into Application Development',
    validate: (value: MonthYear | null): string | undefined => {
      // First run the standard validation
      const baseError = validateCapitalizationStartMonthYear(ascProject, capitalizationEndMonthYear)(value);
      if (baseError) return baseError;

      // Then check lock boundary (only if field is not already locked and value exists)
      if (!isStartDateLocked && value && lockBoundaryMonthYear) {
        if (isMonthYearBeforeOrEqual(value, lockBoundaryMonthYear)) {
          return `Must be after ${formattedLockBoundary} (locked period)`;
        }
      }
    },
  };

  // End date field configuration
  const endDate: CapitalizationFieldConfig = {
    isLocked: isEndDateLocked,
    disabled:
      !ascProject.startDate ||
      isEndDateLocked ||
      (capitalizationStartMonthYear === null && capitalizationEndMonthYear === null),
    clearable: !isEndDateLocked,
    // If end date is locked, only use cap start as min
    // If not locked, minimum is the later of: cap start date or lock boundary
    min: isEndDateLocked
      ? (capitalizationStartMonthYear ?? undefined)
      : capitalizationStartMonthYear && lockBoundaryMonthYear
        ? maxMonthYear(capitalizationStartMonthYear, lockBoundaryMonthYear)
        : (lockBoundaryMonthYear ?? capitalizationStartMonthYear ?? undefined),
    max: undefined, // No max constraint for end date
    helpText:
      isEndDateLocked && formattedLockBoundary
        ? `This field cannot be changed because it is in a closed period (before ${formattedLockBoundary}).`
        : 'Transition from Application Development into GA',
    validate: (value: MonthYear | null): string | undefined => {
      // First run the standard validation
      const baseError = validateCapitalizationEndMonthYear(ascProject, capitalizationStartMonthYear)(value);
      if (baseError) return baseError;

      // Then check lock boundary (only if field is not already locked and value exists)
      if (!isEndDateLocked && value && lockBoundaryMonthYear) {
        if (isMonthYearBefore(value, lockBoundaryMonthYear)) {
          return `Must be on or after ${formattedLockBoundary} (locked period)`;
        }
      }
    },
  };

  return { status, startDate, endDate };
};
